// src/stores/useAuth.ts - version 0.1
import { defineStore } from "pinia";
import { useProfile } from "@/stores/models/useProfile";
import api from "../plugins/axios";

export const useAuthStore = defineStore("auth", {
  state: () => ({
    sessionChecked: false as boolean,
    isLoggedIn: false as boolean,
    userId: null as number | null,
    accessType: null as string | null,
    lastChecked: null as number | null, // NEW: Track last check time
  }),

  getters: {
    // NEW: Check if session needs refresh (every 10 minutes)
    needsSessionRefresh(): boolean {
      if (!this.lastChecked) return true;
      const tenMinutes = 10 * 60 * 1000;
      return Date.now() - this.lastChecked > tenMinutes;
    },
  },

  actions: {
    /**
     * Hits the API only once per app session.
     * Subsequent calls return immediately unless forced.
     */
    async checkSession(force = false) {
      // Skip if already checked recently (unless forced)
      if (!force && this.sessionChecked && !this.needsSessionRefresh && this.isLoggedIn) {
        return true;
      }

      try {
        const { data } = await api.get("/api/get-profile.php", {
          withCredentials: true,
          timeout: 5000, // NEW: Faster timeout for session checks
        });
        
        if (data.success) {
          this.userId = data.user.id;
          this.accessType = data.user.access_type;
          this.isLoggedIn = true;
          this.lastChecked = Date.now(); // NEW: Update last check time

          const profileStore = useProfile();
          profileStore.setProfile(data.user);
          profileStore.profileChecked = true;
          return true; // NEW: Return success status
        } else {
          this.clearAuth(); // NEW: Use helper method
          return false;
        }
      } catch (error: any) {
        // NEW: Only clear auth on 401/403 errors
        if (error.response?.status === 401 || error.response?.status === 403) {
          this.clearAuth();
        }
        return false;
      } finally {
        this.sessionChecked = true;
      }
    },

    /**
     * Call on logout so that the next protected navigation
     * will re-verify with the server.
     */
    async logout() {
      try {
        // NEW: Call logout endpoint
        await api.post("/api/logout.php");
      } catch (error) {
        console.error("Logout error:", error);
      } finally {
        // Clear local state regardless
        this.clearAuth();
        
        // NEW: Clear profile store
        const profileStore = useProfile();
        profileStore.resetProfile();
      }
    },

    // NEW: Helper to clear auth state
    clearAuth() {
      this.sessionChecked = false;
      this.isLoggedIn = false;
      this.userId = null;
      this.accessType = null;
      this.lastChecked = null;
    },
  },
});